import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Line } from "@react-three/drei";
import * as THREE from "three";
import { SingleLineType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { ThreeEvent } from "@react-three/fiber";
import { editorStore } from "../../store/editorStore";
import { MeasurementLine } from "./MeasurementLine";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { getQuadraticBezierPoint } from "../../utils/arcUtils";

interface SingleLineProps {
    line: SingleLineType;
    color: string;
    onLinePointerDown: (event: ThreeEvent<PointerEvent>, id: string) => void;
    onLinePointerEnter: (event: ThreeEvent<PointerEvent>, id: string) => void;
    onLinePointerLeave: (event: ThreeEvent<PointerEvent>, id: string) => void;
    handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>, line: SingleLineType) => void;
    handleLengthClick: (lineId: string) => void;
}

const SingleLine: React.FC<SingleLineProps> = observer(({
    line,
    color,
    onLinePointerDown,
    onLinePointerEnter,
    onLinePointerLeave,
    handleKeyPress,
    handleLengthClick
}) => {
    const zoomLevel = editorStore.zoomLevel; // Rerenender when zoom level changes
    const noopRaycast = () => null;
    const lineHitWidth = useMemo(() => {
        const divisor = editorStore.zoomLevelDivisor();
        const zoomScale = Number.isFinite(divisor) && divisor > 1 ? divisor : 1;
        return Math.max(0.01, 0.15 / zoomScale);
    }, [zoomLevel]);

    const linePoints = useMemo(() => {
        if (!line || !line.start || !line.end) {
            return [];
        }
        if (line.lineForm === "arc" && line.controlPoint) {
            return Array.from({ length: 25 }, (_, index) => {
                const point = getQuadraticBezierPoint(index / 24, line.start, line.controlPoint!, line.end);
                return new THREE.Vector3(point.x, point.y, 0);
            });
        }

        return [
            new THREE.Vector3(line.start.x, line.start.y, 0),
            new THREE.Vector3(line.end.x, line.end.y, 0),
        ];
    }, [
        line?.lineForm,
        line?.controlPoint?.x,
        line?.controlPoint?.y,
        line?.start?.x,
        line?.start?.y,
        line?.end?.x,
            line?.end?.y,
    ]);
    const curvedHitAreaGeometry = useMemo(() => {
        if (!line || line.lineForm !== "arc" || linePoints.length < 2) {
            return null;
        }

        const halfWidth = lineHitWidth / 2;
        const vertices: number[] = [];
        const indices: number[] = [];

        linePoints.forEach((point, index) => {
            const previous = linePoints[Math.max(0, index - 1)];
            const next = linePoints[Math.min(linePoints.length - 1, index + 1)];
            const tangent = new THREE.Vector2(next.x - previous.x, next.y - previous.y);

            if (tangent.lengthSq() < 1e-12) {
                tangent.set(1, 0);
            } else {
                tangent.normalize();
            }

            const normal = new THREE.Vector2(-tangent.y, tangent.x).multiplyScalar(halfWidth);
            vertices.push(
                point.x + normal.x, point.y + normal.y, 0,
                point.x - normal.x, point.y - normal.y, 0,
            );

            if (index < linePoints.length - 1) {
                const base = index * 2;
                indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
            }
        });

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        return geometry;
    }, [line, lineHitWidth, linePoints]);

    // Return null if line or start is null
    if (!line || !line.start || !line.end) {
        return null;
    }

    return (
        <group
            onPointerDown={(e) => onLinePointerDown(e, line.id)}
            onPointerEnter={!renderStore.isDragging && !editorStore.lineDragging ? (e) => onLinePointerEnter(e, line.id) : undefined}
            onPointerLeave={!renderStore.isDragging && !editorStore.lineDragging ? (e) => onLinePointerLeave(e, line.id) : undefined}
            position={[0, 0, (line.zIndex || 0) * zIndexEpsilon]}
        >
            {curvedHitAreaGeometry ? (
                <mesh geometry={curvedHitAreaGeometry}>
                    <meshBasicMaterial transparent opacity={0.0} side={THREE.DoubleSide} />
                </mesh>
            ) : (
                <mesh
                    position={[
                        (line.start.x + line.end.x) / 2,
                        (line.start.y + line.end.y) / 2,
                        0
                    ]}
                    rotation={[
                        0,
                        0,
                        new THREE.Vector2(line.end.x - line.start.x, line.end.y - line.start.y).angle()
                    ]}
                >
                    <planeGeometry args={[line.start.distanceTo(line.end), lineHitWidth]} />
                    <meshBasicMaterial
                        transparent
                        opacity={0.0}
                    />
                </mesh>
            )}
            <Line
                key={`group-line-${line.id}`}
                points={linePoints}
                color={color}
                lineWidth={line.lineWeight || floorplannerStore.singleLineWeight}
                {...(line.lineType === "dashed" && {
                    dashed: true,
                    dashSize: zoomLevel < 0.5 ? 0 : 0.15,
                    gapSize: zoomLevel < 0.5 ? 0 : 0.1,
                })}
                raycast={noopRaycast}
            />
            {!line.hideMeasurement && (editorStore.showMeasures || line?.selected || (editorStore.selections.length && editorStore.selections[0].id === line.id)) && (
                <MeasurementLine
                    object={line}
                />
            )}
        </group>
    );
});

export default React.memo(SingleLine, (prevProps, nextProps) => {
    // Only re-render if any of these props change
    return (
        prevProps.line.lastUpdate === nextProps.line.lastUpdate &&
        prevProps.line.start.x === nextProps.line.start.x &&
        prevProps.line.start.y === nextProps.line.start.y &&
        prevProps.line.end.x === nextProps.line.end.x &&
        prevProps.line.end.y === nextProps.line.end.y &&
        prevProps.line.lineForm === nextProps.line.lineForm &&
        prevProps.line.controlPoint?.x === nextProps.line.controlPoint?.x &&
        prevProps.line.controlPoint?.y === nextProps.line.controlPoint?.y &&
        prevProps.line.selected === nextProps.line.selected &&
        prevProps.color === nextProps.color &&
        prevProps.onLinePointerDown === nextProps.onLinePointerDown &&
        prevProps.onLinePointerEnter === nextProps.onLinePointerEnter &&
        prevProps.onLinePointerLeave === nextProps.onLinePointerLeave &&
        prevProps.handleKeyPress === nextProps.handleKeyPress &&
        prevProps.handleLengthClick === nextProps.handleLengthClick
    );
});
